/**
 * IXD 편집 세션의 셀 식별·메타데이터 인덱스.
 *
 * 백엔드가 table cell에 내려준 `column.id`를 편집/DOM 경계의 1차 식별자로 쓴다.
 * cellId 정규형은 4-segment ``{periodType}:{lineItemId}:{axis}:{accumulatedType}``
 * — 백엔드 ``xbrl.cell_id.CellId`` 와 동일 계약. accumulatedType 이 비어 있어도
 * trailing colon 을 유지해 segment 수를 일정하게 둔다.
 */

import type {
  CellChangeMetadata,
  IxdChunk,
  TableColumn,
  TaggedValue,
} from "@/apis/ixd-editor/types";

/**
 * 4-segment cellId 의 의미 단위 추출.
 *
 * 입력은 `{periodType}:{lineItemId}:{axis}:{accumulatedType}` 정규형을 가정한다
 * (raw 형식이면 먼저 `normalizeCellId` / `applyPeriodTypeToCellId` 로 정규화).
 * 누락된 segment 는 빈 문자열로, axisIds 는 `|` 로 분리되어 배열로 반환된다.
 */
export interface ParsedCellId {
  periodType: string;
  lineItemId: string;
  axisIds: string[];
  accumulatedType: string;
}

export const parseCellId = (cellId: string): ParsedCellId => {
  const [periodType = "", lineItemId = "", axisStr = "", accumulatedType = ""] =
    cellId.split(":");
  const axisIds = axisStr ? axisStr.split("|").filter(Boolean) : [];
  return { periodType, lineItemId, axisIds, accumulatedType };
};

/**
 * 4-segment cellId의 axis 부분만 사전순 정렬.
 * `{period}:{lineItem}:{a|c|b}:{acc}` → `{period}:{lineItem}:{a|b|c}:{acc}`.
 */
export const normalizeCellId = (cellId: string): string => {
  const parts = cellId.split(":");
  if (parts.length < 3) return cellId;
  const axisPart = parts[2];
  if (axisPart) {
    parts[2] = axisPart.split("|").sort().join("|");
  }
  return parts.join(":");
};

export const applyPeriodTypeToCellId = (
  cellId: string,
  periodType: string,
): string => {
  const segments = cellId.split(":");
  let result: string[];
  if (segments.length === 1) {
    result = [periodType, cellId, "", ""];
  } else if (segments.length === 2) {
    result = [periodType, segments[0], segments[1], ""];
  } else {
    result = [periodType, ...segments.slice(1)];
    while (result.length < 4) result.push("");
    result = result.slice(0, 4);
  }
  return normalizeCellId(result.join(":"));
};

export const buildCellIdLookupCandidates = (
  periodType: string,
  canonicalCellId: string | null | undefined,
  ...cellIds: Array<string | null | undefined>
): string[] => {
  const candidates: string[] = [];
  const seen = new Set<string>();
  const add = (cellId: string) => {
    if (seen.has(cellId)) return;
    seen.add(cellId);
    candidates.push(cellId);
  };

  if (canonicalCellId) add(canonicalCellId);
  for (const cellId of cellIds) {
    if (!cellId) continue;
    add(normalizeCellId(cellId));
    add(applyPeriodTypeToCellId(cellId, periodType));
  }

  return candidates;
};

/** TaggedValue → 정규화된 4-segment cellId */
export const buildCellIdFromTaggedValue = (
  periodType: string,
  tv: TaggedValue,
): string =>
  normalizeCellId(
    `${periodType}:${tv.lineItemId}:${tv.axisIds.join("|")}:${tv.accumulatedType ?? ""}`,
  );

export const buildCellIdAliasesFromTaggedValue = (
  periodType: string,
  tv: TaggedValue,
): string[] => {
  // 현재는 values의 canonical id만 alias로 사용한다. contextKey까지 alias로 섞으면
  // 표 셀의 metadata context와 값 metadata context를 잘못 매칭할 수 있어 배열 형태만 유지한다.
  return [buildCellIdFromTaggedValue(periodType, tv)];
};

export const buildCellIdFromTableColumn = (
  periodType: string,
  column: TableColumn,
): string | null => {
  if (column.id) return normalizeCellId(column.id);
  const contextKey = column.attributes?.contextKey;
  return contextKey ? applyPeriodTypeToCellId(contextKey, periodType) : null;
};

export const CELL_IDENTITY_FIELDS = [
  "elementName",
  "elementNamespace",
  "domainName",
  "domainNamespace",
  "dimensionName",
  "dimensionNamespace",
  "csType",
  "languageType",
  "contextKey",
] as const satisfies readonly (keyof CellChangeMetadata)[];

export type CellIdentityField = (typeof CELL_IDENTITY_FIELDS)[number];
export type CellIdentityMetadata = Pick<CellChangeMetadata, CellIdentityField>;
export type CellIdentityKey = string;

/**
 * 키 생성에 필요한 최소 형태.
 *
 * 값은 어차피 문자열로 직렬화되므로 csType 같은 좁은 union을 요구하지 않는다.
 * 덕분에 같은 9필드를 조금 다른 이름·타입으로 내려주는 응답(값 동기화 plan 등)도
 * 키 생성 로직을 복제하지 않고 이 함수 하나를 그대로 쓸 수 있다 — 직렬화 규칙이
 * 두 곳에 생기면 한 글자 차이로 조용히 다른 fact가 된다.
 */
export type CellIdentityMetadataLike = Readonly<
  Record<CellIdentityField, string | null | undefined>
>;

const normalizeIdentityValue = (value: string | null | undefined): string =>
  value === null || value === undefined ? "" : String(value);

const AXIS_SEPARATOR = "|";

/** 축·멤버를 담는 4개 필드. 같은 인덱스끼리 한 쌍이다. */
const AXIS_FIELDS = [
  "dimensionName",
  "dimensionNamespace",
  "domainName",
  "domainNamespace",
] as const satisfies readonly CellIdentityField[];

const splitAxisField = (value: string | null | undefined): string[] =>
  value ? String(value).split(AXIS_SEPARATOR) : [];

/** 네 값을 모두 이어 비교해 정렬을 완전히 결정적으로 만든다. */
const axisPairSortKey = (pair: readonly string[]): string =>
  pair.join("\u0000");

/**
 * 축·멤버 쌍을 한 기준으로 다시 정렬한다.
 *
 * XBRL 컨텍스트의 dimension은 집합이라 순서에 의미가 없는데, 만드는 쪽마다
 * 정렬 기준이 달라 같은 fact가 다른 문자열로 온다 — 값 동기화 plan은 축
 * 이름으로, 셀 metadata는 멤버 이름으로 정렬해 내려준다. 문자열을 그대로
 * 키에 넣으면 같은 fact가 서로 다른 fact가 되고, 에러 없이 "대응 셀 없음"
 * 으로만 보인다.
 *
 * 네 필드의 길이가 어긋나면 쌍을 만들 수 없으므로 원본을 그대로 둔다 —
 * 잘못 섞어 축과 멤버를 어긋나게 하는 것보다 안전하다.
 */
const canonicalizeAxisPairs = (
  metadata: CellIdentityMetadataLike,
): Partial<Record<CellIdentityField, string>> => {
  const columns = AXIS_FIELDS.map((field) => splitAxisField(metadata[field]));
  const [dimensionNames] = columns;
  const count = dimensionNames.length;
  if (count <= 1 || columns.some((column) => column.length !== count)) {
    return {};
  }

  const pairs = Array.from({ length: count }, (_, index) =>
    columns.map((column) => column[index]),
  );
  pairs.sort((a, b) => {
    const keyA = axisPairSortKey(a);
    const keyB = axisPairSortKey(b);
    return keyA < keyB ? -1 : keyA > keyB ? 1 : 0;
  });

  return Object.fromEntries(
    AXIS_FIELDS.map((field, fieldIndex) => [
      field,
      pairs.map((pair) => pair[fieldIndex]).join(AXIS_SEPARATOR),
    ]),
  );
};

/**
 * metadata 9필드(축 포함)로 만드는 fact 단위 식별자. cellId와 달리 전역에서 unique하다.
 */
export const makeCellIdentityKey = (
  metadata: CellIdentityMetadataLike,
): CellIdentityKey => {
  const canonical = { ...metadata, ...canonicalizeAxisPairs(metadata) };
  return JSON.stringify(
    CELL_IDENTITY_FIELDS.map((field) => [
      field,
      normalizeIdentityValue(canonical[field]),
    ]),
  );
};

/**
 * 편집 세션의 1차 키.
 *
 * cellId는 축을 담지 않아 unique하지 않다 — 축만 다른 두 표의 셀은 cellId가 완전히 같다.
 * 그래서 identity를 아는 셀은 identity로 키잉하고, identity를 못 찾은 셀(문장영역 등)만
 * cellId로 떨어뜨린다. prefix로 두 종류를 구분해 서로 충돌하지 않게 한다.
 *
 * identityKey에는 namespace URL의 `:`가 들어 있으므로 이 키에 normalizeCellId를 적용하면
 * 안 된다 — cellId 전용 정규화가 identityKey를 훼손한다.
 */
/** 변경이 어디서 왔나: 사용자 직접 입력 / 값 일괄 적용. 셀 모델 소비자(미리보기)도 쓰는 타입이라 여기 둔다. */
export type CellChangeOrigin = "manual" | "value-apply";

export type ChangeKey = string;

export const identityChangeKey = (identityKey: CellIdentityKey): ChangeKey =>
  `identity:${identityKey}`;

export const cellChangeKey = (cellId: string): ChangeKey =>
  `cell:${normalizeCellId(cellId)}`;

export const makeChangeKey = (
  identityKey: CellIdentityKey | undefined | null,
  cellId: string,
): ChangeKey =>
  identityKey ? identityChangeKey(identityKey) : cellChangeKey(cellId);

/** ChangeKey가 identity 기반이면 identityKey를, cellId 기반이면 undefined를 반환. */
export const parseIdentityChangeKey = (
  changeKey: ChangeKey,
): CellIdentityKey | undefined =>
  changeKey.startsWith("identity:")
    ? changeKey.slice("identity:".length)
    : undefined;

/** ChangeKey가 cellId 기반이면 정규화된 cellId를 반환한다. */
export const parseCellIdChangeKey = (
  changeKey: ChangeKey,
): string | undefined =>
  changeKey.startsWith("cell:") ? changeKey.slice("cell:".length) : undefined;

export interface CellIndexEntry {
  metadata: CellChangeMetadata;
  /** 미저장 변경 판정의 서버 기준값. */
  baselineValue: string;
}

/**
 * chunks를 순회해 ChangeKey → {metadata, baseline} 맵 생성.
 *
 * identity를 아는 셀은 identity로 키잉하므로 축만 다른 두 표의 셀이 충돌하지 않는다.
 * 예전처럼 cellId만으로 키잉하면 나중에 순회된 dlView가 앞선 것을 덮어써, 편집 시
 * 엉뚱한 축의 metadata로 저장된다.
 */
export const buildCellIndex = (
  chunks: IxdChunk[],
): Map<ChangeKey, CellIndexEntry> => {
  const map = new Map<ChangeKey, CellIndexEntry>();
  for (const chunk of chunks) {
    for (const dlView of chunk.dlViews) {
      const dlViewValueEntries = new Map<string, CellIndexEntry>();
      if (dlView.values) {
        for (const [periodType, taggedValues] of Object.entries(
          dlView.values,
        )) {
          for (const tv of taggedValues) {
            const entry = {
              metadata: tv.metadata,
              baselineValue: tv.value,
            };
            map.set(identityChangeKey(makeCellIdentityKey(tv.metadata)), entry);
            for (const cellId of buildCellIdAliasesFromTaggedValue(
              periodType,
              tv,
            )) {
              map.set(cellChangeKey(cellId), entry);
              dlViewValueEntries.set(cellId, entry);
            }
          }
        }
      }
      if (dlView.table) {
        for (const [periodType, rows] of Object.entries(dlView.table)) {
          for (const row of rows) {
            for (const column of row.columns) {
              if (column.tagName !== "td" || !column.id) continue;
              const canonicalCellId = buildCellIdFromTableColumn(
                periodType,
                column,
              );
              const candidates = buildCellIdLookupCandidates(
                periodType,
                canonicalCellId,
                column.id,
                column.attributes.contextKey,
              );
              const entry = candidates
                .map((cellId) => dlViewValueEntries.get(cellId))
                .find((value): value is CellIndexEntry => value !== undefined);
              if (!entry) continue;
              map.set(cellChangeKey(column.id), entry);
              if (canonicalCellId)
                map.set(cellChangeKey(canonicalCellId), entry);
            }
          }
        }
      }
    }
  }
  return map;
};

/** 값 셀 판정: 백엔드가 id 를 준 td 만 값 셀이다 (th, id 없는 td 는 라벨·여백). */
export const isValueCell = (column: TableColumn): boolean =>
  column.tagName === "td" && !!column.id;
